import React, { memo, useEffect, useMemo, useRef, useState, Suspense, lazy } from "react";
import {
  Box,
  Button,
  Center,
  HStack,
  ModalBody,
  ModalFooter,
  Spinner,
  Text,
} from "@chakra-ui/react";

const AffectationMap = lazy(() =>
  import("../missions/components/AffectationMap").then((module) => ({
    default: module.default,
  }))
);

const AffectationModalPanel = memo(function AffectationModalPanel({
  vehicles,
  missionLocation,
  ambulanciers,
  fetchVehicleLocation,
  isSubmitting,
  isReloading = false,
  onReload,
  onCancel,
  onConfirm,
  confirmLabel = "Créer la mission",
}) {
  const [selectedAmbulanceId, setSelectedAmbulanceId] = useState("");
  const staffSelectionRef = useRef({ doctorId: "", nurseId: "", commentaire: "" });

  const handleAmbulanceSelect = (ambulanceId) => {
    setSelectedAmbulanceId((prev) => {
      const next = ambulanceId ? String(ambulanceId) : "";
      if (!next || String(prev) === next) {
        staffSelectionRef.current = { doctorId: "", nurseId: "", commentaire: "" };
      }
      return String(prev) === next && next ? "" : next;
    });
  };

  useEffect(() => {
    setSelectedAmbulanceId("");
    staffSelectionRef.current = { doctorId: "", nurseId: "", commentaire: "" };
  }, [vehicles]);

  const selectedVehicle = useMemo(
    () => vehicles.find((v) => String(v.id) === String(selectedAmbulanceId)) || null,
    [vehicles, selectedAmbulanceId]
  );

  const handleConfirm = () => {
    onConfirm({
      selectedAmbulanceId,
      selectedDoctorId: staffSelectionRef.current.doctorId || "",
      selectedNurseId: staffSelectionRef.current.nurseId || "",
      commentaire: String(staffSelectionRef.current.commentaire || "").trim(),
      vehicle: selectedVehicle,
    });
  };

  if (!missionLocation) {
    return (
      <ModalBody p="16px">
        <Box border="1px solid" borderColor="gray.200" borderRadius="10px" p="16px" bg="gray.50">
          <Text color="red.500" fontSize="14px">
            Position du dossier indisponible. Vérifiez le champ `loc` de ce dossier.
          </Text>
        </Box>
      </ModalBody>
    );
  }

  return (
    <>
      <ModalBody p="16px">
        <Suspense
          fallback={
            <Center h="78vh" minH="520px">
              <Spinner size="lg" color="#459CE9" />
            </Center>
          }
        >
          <AffectationMap
            vehicles={vehicles}
            missionLocation={missionLocation}
            selectedAmbulanceId={selectedAmbulanceId}
            onAmbulanceSelect={handleAmbulanceSelect}
            ambulanciers={ambulanciers}
            fetchVehicleLocation={fetchVehicleLocation}
            staffSelectionRef={staffSelectionRef}
            typeAmbulance={selectedVehicle?.type_vehicule || "TAR"}
            containerHeight="78vh"
          />
        </Suspense>
      </ModalBody>
      <ModalFooter>
        <HStack spacing="10px" w="100%" justify="flex-end">
          {onReload ? (
            <Button
              variant="outline"
              onClick={onReload}
              isLoading={isReloading}
              isDisabled={isSubmitting}
              mr="auto"
            >
              Recharger
            </Button>
          ) : null}
          <Button variant="outline" onClick={onCancel} isDisabled={isSubmitting || isReloading}>
            Annuler
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleConfirm}
            isLoading={isSubmitting}
            isDisabled={!selectedAmbulanceId || isReloading}
          >
            {confirmLabel}
          </Button>
        </HStack>
      </ModalFooter>
    </>
  );
});

export default AffectationModalPanel;